import { Merchant } from "@/dto/merchant";
import { ScanToOrderCartHeader } from "./scanToOrderCartHeader";
import { SendToKitchenButton } from "./sendToKitchenButton";
import { TableData } from "@/dto/tableData";
import { ScanToOrderCartContent } from "./scanToOrderCartContent";
import ReCAPTCHAComponent from "react-google-recaptcha";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localStorageUtils";
import { RestaurantCartItem } from "@/models/cart/types";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { ScanToOrderInfo } from "@/dto/scanToOrderInfo";
import {
  getCartButtonText,
  goToGuestCheckout,
  handleS2OTableData,
  sendToKitchen,
} from "@/utils/scanToOrderUtils";
import { useRouter } from "next13-progressbar";
import { LocalStorageKey } from "@/utils/storageKeys";
import { DrawerModal } from "@/components/modals/modals";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import { DayPart } from "@/dto/dayPartMenu";
import { RestaurantCart } from "@/models/cart/restaurantCart";
import { isEmpty } from "lodash";
import { useLocale, useTranslations } from "next-intl";
import validateTableId from "@/dataLayer/validateTableId";

export interface ScanToOrderCartProps {
  isOpen?: boolean;
  onRequestClose: () => void;
  merchant: Merchant;
  tableData: TableData;
  setIsSentToKitchen: Dispatch<SetStateAction<boolean>>;
  scanToOrderInfo?: ScanToOrderInfo;
  isRunningTabsEnabled: boolean;
  selectedDayPart?: DayPart | null;
}

export const ScanToOrderCart = ({
  isOpen,
  onRequestClose,
  merchant,
  tableData,
  setIsSentToKitchen,
  scanToOrderInfo,
  isRunningTabsEnabled,
  selectedDayPart,
}: ScanToOrderCartProps) => {
  const t = useTranslations("ScanToOrderCart");
  const locale = useLocale();

  const orderSpecialInstructions = tableData?.order?.note;
  const containsSpecialInstructions = !!tableData?.order?.note;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showSpecialInstructions, setShowSpecialInstructions] = useState(
    containsSpecialInstructions ? true : false
  );
  const [failedToSendToKitchen, setFailedToSendToKitchen] =
    useState<boolean>(false);
  const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || "";
  const [specialInstructions, setSpecialInstructions] = useState(
    containsSpecialInstructions ? orderSpecialInstructions : ""
  );
  const [invalidItemsCriteria, setInvalidItemsCriteria] = useState<
    "past" | "inactive" | null
  >(null);
  const merchantColor =
    merchant.merchantMeta?.colorPalette?.color ||
    cloverColorTheme.Main.primaryColor;
  const [cartItems, setCartItems] = useState(() => {
    return getFromLocalStorage<RestaurantCartItem[]>(
      LocalStorageKey.RESTAURANT_CART,
      []
    );
  });
  const router = useRouter();

  const captchaRef = useRef<ReCAPTCHAComponent | null>(null);

  const titleId = useId();

  const currentItemCount = cartItems.reduce((totalQuantity, item) => {
    const quantity = item.value?.quantity || 0;
    return totalQuantity + quantity;
  }, 0);

  const sentToKitchenCart = tableData?.order?.cart?.items
    ? tableData?.order?.cart?.items
    : [];

  const isBusinessClosed = !isBusinessOpen(merchant, true);
  const isButtonDisabled =
    (sentToKitchenCart?.length === 0 && cartItems?.length === 0) ||
    isBusinessClosed ||
    isLoading ||
    invalidItemsCriteria === "inactive" ||
    invalidItemsCriteria === "past";

  const buttonText = getCartButtonText(
    currentItemCount,
    merchant.isRunningTabsEnabled,
    t
  );

  const emptyCart = currentItemCount === 0;

  useEffect(() => {
    const storedCart = getFromLocalStorage<RestaurantCartItem[]>(
      LocalStorageKey.RESTAURANT_CART,
      []
    );
    setCartItems(storedCart);
    setFailedToSendToKitchen(false);
  }, [isOpen]);

  useEffect(() => {
    if (isEmpty(cartItems)) {
      setInvalidItemsCriteria(null);
    }
    saveToLocalStorage(LocalStorageKey.RESTAURANT_CART, cartItems);
  }, [cartItems]);

  const onSendToKitchenClick = useCallback(async () => {
    /**
     * If daypart exists, check if the items belong to the chosen daypart menu
     * or the chose daypart is still active before proceeding with checkout/sending to kitchen
     */
    if (!isEmpty(selectedDayPart)) {
      const cartItemsStatus = RestaurantCart.getRestaurantCartItemsStatus(
        cartItems,
        true,
        selectedDayPart,
        merchant.timezone
      );

      if (cartItemsStatus !== "active") {
        setInvalidItemsCriteria(cartItemsStatus);
        return;
      }
    }

    if (merchant?.isRunningTabsEnabled && !emptyCart) {
      initiateCaptcha();
    } else {
      setIsLoading(true);
      const tableData = await validateTableId(merchant.id, tableId);
      handleS2OTableData(tableData, "invalidTable");
      goToGuestCheckout(router, scanToOrderInfo);
      setIsLoading(false);
    }
  }, [
    cartItems,
    selectedDayPart,
    merchant,
    emptyCart,
    router,
    scanToOrderInfo,
  ]);

  if (!scanToOrderInfo) {
    return null;
  }
  const { orderType, tableId, employeeId } = scanToOrderInfo;

  const removeFromCart = (event: React.MouseEvent, index: number) => {
    event.stopPropagation();
    setCartItems((prevCartItems: any[]) =>
      prevCartItems.filter((_: any, i: any) => i !== index)
    );
  };

  async function onRecaptchaSubmit(value: any) {
    setIsLoading(true);
    await sendToKitchen(
      merchant,
      value,
      cartItems,
      specialInstructions,
      tableId,
      merchant.id,
      tableData,
      onRequestClose,
      setIsSentToKitchen,
      setFailedToSendToKitchen,
      setIsLoading,
      employeeId,
      selectedDayPart?.menuId
    );
  }

  const initiateCaptcha = () => {
    if (failedToSendToKitchen) {
      setFailedToSendToKitchen(false);
    }
    captchaRef.current?.reset();
    captchaRef.current?.execute();
  };

  return (
    <DrawerModal
      isOpen={isOpen ?? false}
      onRequestClose={onRequestClose}
      contentClassName="flex flex-col h-full"
      aria={{ labelledby: titleId }}
      focusElementId={titleId}
    >
      <ScanToOrderCartHeader titleId={titleId} />
      <ScanToOrderCartContent
        tableData={tableData}
        cartItems={cartItems}
        showSpecialInstructions={showSpecialInstructions}
        setShowSpecialInstructions={setShowSpecialInstructions}
        merchantColor={merchantColor}
        setSpecialInstructions={setSpecialInstructions}
        removeFromCart={removeFromCart}
        orderType={orderType}
        isCartEmpty={emptyCart}
        isRunningTabsEnabled={isRunningTabsEnabled}
        failedToSendToKitchen={failedToSendToKitchen}
        onRequestClose={onRequestClose}
        specialInstructions={specialInstructions}
        isBusinessClosed={isBusinessClosed}
        invalidItemsCriteria={invalidItemsCriteria}
      />
      <style>
        {`
        .grecaptcha-badge { margin-bottom: 50px; }
        `}
      </style>
      <SendToKitchenButton
        buttonText={buttonText}
        merchantColor={merchantColor}
        isButtonDisabled={isButtonDisabled}
        onClick={onSendToKitchenClick}
      />
      <ReCAPTCHAComponent
        className=""
        sitekey={recaptchaKey}
        ref={captchaRef}
        onChange={onRecaptchaSubmit}
        size="invisible"
        badge="bottomleft"
        hl={locale}
      />
    </DrawerModal>
  );
};
